import { useFetch } from "@app/framework/hooks";
import { apiUrls } from "@app/shared";
import { Loading, NoDataFound } from "@modules/shared/components";
import { getLocalizedValue } from "@modules/shared/utils";
import { trans } from "@mongez/localization";
import { isNumeric } from "@mongez/supportive-is";
import { useEffect, useState } from "react";
import { FinancialInformationItem } from "../types";
import { YearsTabs } from "./YearsTabs";

export function FinancialInfoStatementsTab() {
  const [filteredStatements, setFinancialStatements] = useState<
    FinancialInformationItem[]
  >([]);

  const { load, isLoading, data } = useFetch<{
    records: FinancialInformationItem[];
    years: number[];
  }>(apiUrls.financialInformation.financialStatements);

  useEffect(() => {
    load().then((data) => {
      setFinancialStatements(data?.records || []);
    });
  }, []);

  const handleYearChange = (year: number | null) => {
    if (year === null) {
      setFinancialStatements(data?.records || []);
      return;
    }

    const filteredStatements =
      data?.records.filter((record) => record.year === year) || [];
    setFinancialStatements(filteredStatements);
  };

  return (
    <div className="flex animate-fade flex-col gap-8">
      <div className="flex flex-col gap-6 rounded-3xl bg-white p-4 md:gap-8 md:p-8">
        <YearsTabs years={data?.years || []} onChange={handleYearChange} />
        {isLoading ? (
          <Loading />
        ) : filteredStatements.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
            {filteredStatements.map((record, index) => (
              <StatementItem key={index} record={record} />
            ))}
          </div>
        ) : (
          <NoDataFound />
        )}
      </div>
    </div>
  );
}

type StatementItemProps = {
  record: FinancialInformationItem;
};

function StatementItem({ record }: StatementItemProps) {
  const title = record.title["en"].split(" ")[2].toLocaleLowerCase();

  return (
    <a
      data-fancybox="financial-statements"
      data-src={getLocalizedValue(record.pdf)}
      href={getLocalizedValue(record.pdf)}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex animate-fade cursor-pointer flex-col items-center gap-4 rounded-2xl border border-[#E0E0E0] bg-white p-4 shadow-sm transition-colors duration-300 hover:bg-gray-100"
    >
      <div className="flex flex-col gap-2">
        <p className="text-center font-semibold text-marine">
          {isNumeric(title)
            ? trans(`quarterlyReportKeys.annual`)
            : trans(`quarterlyReportKeys.${title}`)}
        </p>
      </div>
    </a>
  );
}
